import React, { useEffect, useState, useMemo } from "react";
import {
	Modal,
	Table,
	Form,
	Input,
	Space,
	Button,
	Typography,
	message,
	Tag,
	Switch,
} from "antd";
import { get_points_of_sale_users } from "../../../../../../../api/endpoints/pointsofsale";
import { update_user_whatsapp } from "../../../../../../../api/endpoints/users";
import { COLORS } from "../../../../../../layout/theme";
import PhoneInput from "../../../../../../common/phone-input/PhoneInput";
import { getWhatsappTagConfig } from "../../../../../../../utils/whatsappHelpers";

export default function PosUsersModal({
	open,
	onCancel,
	pointOfSaleId,
	onSaved,
	clientId,
	onValidateWhatsappSingle,
}) {
	const [loading, setLoading] = useState(false);
	const [users, setUsers] = useState([]);
	const [selectedUserId, setSelectedUserId] = useState(null);
	const [form] = Form.useForm();
	const [selectedUser, setSelectedUser] = useState(null);

	const hasSelection = !!selectedUserId;

	const [unlockLockedEdit, setUnlockLockedEdit] = useState(false);

	const isSelectedLocked = !!selectedUser?.whatsappLocked;
	const canEditSensitive = !isSelectedLocked || unlockLockedEdit;

	const fetchUsers = async () => {
		if (!pointOfSaleId) return;
		try {
			setLoading(true);
			const res = await get_points_of_sale_users(pointOfSaleId, clientId);
			setUsers(res || []);
			setSelectedUserId(null);
			setSelectedUser(null);
			form.resetFields();
		} catch (e) {
			console.error(e);
			message.error("No se pudieron cargar los usuarios del PDV.");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (open) fetchUsers();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, pointOfSaleId]);

	const columns = useMemo(
		() => [
			{
				title: "Usuario",
				dataIndex: "firstName",
				key: "name",
				render: (_, r) => (
					<Space direction="vertical" size={0}>
						<Typography.Text strong>
							{r.firstName} {r.lastName}
						</Typography.Text>
						<Typography.Text type="secondary" style={{ fontSize: 12 }}>
							{r.email}
						</Typography.Text>
					</Space>
				),
			},
			{
				title: "Estado",
				dataIndex: "optedOutWhatsApp",
				key: "optout",
				width: 120,
				render: (v, r) => {
					const waId = (r.whatsappUserId ?? "").toString().trim();
					const hasWhatsappUser = !!waId;

					const { text, color } = getWhatsappTagConfig(
						hasWhatsappUser,
						r.whatsappValidated,
						r.whatsappLocked,
					);

					return (
						<Space direction="vertical" align="center">
							{v ? (
								<Tag color="red">BAJA</Tag>
							) : (
								<Tag color={color}>{text}</Tag>
							)}
						</Space>
					);
				},
			},
			{ title: "Teléfono", dataIndex: "phone", key: "phone", width: 160 },
			{
				title: "WhatsApp ID",
				dataIndex: "whatsappUserId",
				key: "wa",
				width: 200,
			},
		],
		[],
	);

	const rowSelection = {
		type: "radio",
		selectedRowKeys: selectedUserId ? [selectedUserId] : [],
		onChange: (keys) => {
			const id = keys[0];
			setSelectedUserId(id);

			const u = users.find((x) => x.id === id);
			if (!u) return;

			setSelectedUser(u);

			form.setFieldsValue({
				// ojo: si PhoneInput separa area code, puede que ya lo resuelva internamente.
				phone: (u.phone || "").replace(/\D/g, "").slice(-10) || "",
				whatsappUserId: u.whatsappUserId || "",
				optedOutWhatsApp: !!u.optedOutWhatsApp,
			});
		},
		getCheckboxProps: (record) => ({ value: record.id }),
	};

	const handleSave = async () => {
		try {
			if (!selectedUserId) {
				message.info("Elegí un usuario para editar.");
				return;
			}

			const values = await form.validateFields();
			setLoading(true);

			const payload = {
				id: selectedUserId,
				phone: `${values.phoneAreaCode ?? ""}${values.phone ?? ""}`,
				whatsappUserId:
					"549" + (values.whatsappUserId || "").replace(/\D/g, "").slice(-10),
				optedOutWhatsApp: !!values.optedOutWhatsApp,
				clientId: clientId,
				forceUnlockWhatsapp: isSelectedLocked && unlockLockedEdit,
			};

			await update_user_whatsapp(payload);
			setUnlockLockedEdit(false);
			message.success("Datos del usuario actualizados.");
			await fetchUsers();
			onSaved?.();
		} catch (e) {
			console.error(e);
			message.error("No se pudieron actualizar los datos.");
		} finally {
			setLoading(false);
		}
	};

	const handleValidateSelectedWhatsapp = async () => {
		if (!selectedUser) return message.info("Elegí un usuario.");
		if (!onValidateWhatsappSingle)
			return message.error("No está configurado el validador.");

		const record = {
			id: selectedUser.id,
			userId: selectedUser.id,
			whatsappUserId: selectedUser.whatsappUserId,
		};

		await onValidateWhatsappSingle(record);
		await fetchUsers();
	};

	const handleClose = () => {
		setSelectedUserId(null);
		setSelectedUser(null);
		setUsers([]);
		form.resetFields();
		onCancel?.();
	};

	return (
		<Modal
			open={open}
			title="Usuarios asignados al PDV"
			onCancel={onCancel}
			width={800}
			footer={
				<Space>
					<Button onClick={handleClose}>Cerrar</Button>
					<Button
						onClick={handleValidateSelectedWhatsapp}
						disabled={!selectedUserId || loading}
						loading={loading}
					>
						Validar WhatsApp
					</Button>
					<Button
						type="primary"
						onClick={handleSave}
						disabled={!selectedUserId}
						loading={loading}
					>
						Guardar cambios
					</Button>
				</Space>
			}
			destroyOnClose
		>
			<Table
				size="small"
				loading={loading}
				dataSource={users}
				columns={columns}
				rowKey={(r) => r.id}
				rowSelection={rowSelection}
				pagination={false}
				style={{ marginBottom: 16 }}
			/>
			{hasSelection && isSelectedLocked && (
				<div style={{ marginBottom: 12 }}>
					<Tag color="red">WHATSAPP LOCKED</Tag>

					<Form.Item style={{ marginTop: 8, marginBottom: 0 }}>
						<Switch
							checked={unlockLockedEdit}
							disabled={loading || !canEditSensitive}
							checkedChildren="Edición habilitada"
							unCheckedChildren="Edición bloqueada"
							onChange={(next) => {
								if (!next) {
									setUnlockLockedEdit(false);
									return;
								}

								Modal.confirm({
									title: "Confirmar edición de WhatsApp LOCKED",
									content: (
										<div>
											<Typography.Paragraph style={{ marginBottom: 8 }}>
												Estás por editar un WhatsApp marcado como <b>LOCKED</b>.
											</Typography.Paragraph>
											<Typography.Paragraph style={{ marginBottom: 0 }}>
												Al guardar, este usuario va a quedar <b>sin validar</b>{" "}
												y <b>sin lock</b>. Confirmá solo si estás seguro.
											</Typography.Paragraph>
										</div>
									),
									okText: "Sí, habilitar edición",
									okButtonProps: { danger: true },
									cancelText: "Cancelar",
									onOk: () => setUnlockLockedEdit(true),
									onCancel: () => setUnlockLockedEdit(false),
								});
							}}
						/>
					</Form.Item>

					{!unlockLockedEdit && (
						<Typography.Text type="secondary">
							Para editar el número, primero habilitá la edición.
						</Typography.Text>
					)}
				</div>
			)}
			<Form form={form} layout="vertical">
				<div
					style={{
						opacity: !hasSelection || loading ? 0.6 : 1,
						pointerEvents:
							!hasSelection || canEditSensitive || loading ? "none" : "auto",
					}}
				>
					<PhoneInput
						form={form}
						size="default"
						label="Teléfono de Contacto"
						labelStyle={{ color: COLORS.Primary, fontWeight: "600" }}
						disabled={!hasSelection || loading}
					/>
				</div>
				<Form.Item
					label="Whatsapp"
					name="whatsappUserId"
					rules={[
						{ required: true, message: "Este campo es obligatorio" },
						{
							pattern: new RegExp(/^(?!\s*$)[0-9\s]*$/),
							message: "No se aceptan caracteres especiales. Solo numeros",
						},
					]}
				>
					<Input
						maxLength={120}
						addonBefore="549"
						disabled={!hasSelection || loading || !canEditSensitive}
					/>
				</Form.Item>

				<Form.Item shouldUpdate noStyle>
					{({ getFieldValue, setFieldsValue }) => {
						const isOptedOut = !!getFieldValue("optedOutWhatsApp");

						const handleToggleOptOut = (nextChecked) => {
							if (!nextChecked) {
								setFieldsValue({ optedOutWhatsApp: false });
								return;
							}

							Modal.confirm({
								title: "Confirmar BAJA de comunicaciones",
								content: (
									<div>
										<Typography.Paragraph style={{ marginBottom: 8 }}>
											El usuario quedará marcado como <b>BAJA</b> y{" "}
											<b>no recibirá más mensajes</b> por WhatsApp.
										</Typography.Paragraph>
										<Typography.Paragraph style={{ marginBottom: 0 }}>
											Para reactivar, podrás volver a ponerlo en <b>ACTIVO</b>.
										</Typography.Paragraph>
									</div>
								),
								okText: "Confirmar BAJA",
								okButtonProps: { danger: true },
								cancelText: "Cancelar",
								onOk: () => {
									setFieldsValue({ optedOutWhatsApp: true });
								},
								onCancel: () => {
									setFieldsValue({ optedOutWhatsApp: false });
								},
							});
						};

						return (
							<>
								<Form.Item
									label="Estado de comunicaciones"
									name="optedOutWhatsApp"
									valuePropName="checked"
								>
									<Switch
										checkedChildren="BAJA"
										unCheckedChildren="ACTIVO"
										disabled={!hasSelection || loading}
										checked={isOptedOut}
										onChange={handleToggleOptOut}
										style={{
											backgroundColor: isOptedOut
												? COLORS.Error
												: COLORS.Success,
										}}
									/>
								</Form.Item>

								{hasSelection ? (
									<Typography.Text type={isOptedOut ? "danger" : "success"}>
										{isOptedOut
											? "El usuario está dado de BAJA y no recibirá mensajes."
											: "El usuario está ACTIVO y puede recibir comunicaciones."}
									</Typography.Text>
								) : (
									<Typography.Text type="secondary">
										Seleccioná un usuario de la lista para editar.
									</Typography.Text>
								)}
							</>
						);
					}}
				</Form.Item>
			</Form>
		</Modal>
	);
}
